/**
 * Custom-drawn stroke icons. Single-color (currentColor),
 * 24px grid, rounded joins — deliberately simple and consistent, not emoji.
 */
export type IconProps = React.SVGProps<SVGSVGElement>;

function Svg({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-6"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Like actions — a thumbs-up. */
export function ThumbsUpIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </Svg>
  );
}

/** Deck — a flame (the swipe surface; flirty, not romantic). */
export function FlameIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 21c-3.5-2.5-5.5-6-5.5-10 0-4 2.5-6.5 5.5-8.5 3 2 5.5 4.5 5.5 8.5 0 4-2 6.5-5.5 10Z" />
      <path d="M12 21c-1.5-1.2-2.5-3-2.5-5.2 0-2.2 1-3.8 2.5-5.3 1.5 1.5 2.5 3.1 2.5 5.3 0 2.2-1 3.8-2.5 5.2Z" />
    </Svg>
  );
}

/** Filled flame for decorative surfaces (match overlay, badges). */
export function FlameFilledIcon({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M12 21c-3.5-2.5-5.5-6-5.5-10 0-4 2.5-6.5 5.5-8.5 3 2 5.5 4.5 5.5 8.5 0 4-2 6.5-5.5 10Z" />
      <path d="M12 21c-1.5-1.2-2.5-3-2.5-5.2 0-2.2 1-3.8 2.5-5.3 1.5 1.5 2.5 3.1 2.5 5.3 0 2.2-1 3.8-2.5 5.2Z" />
    </svg>
  );
}

/** Browse — a compass, for exploring profiles. */
export function CompassIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m15.5 8.5-2.2 4.8-4.8 2.2 2.2-4.8 4.8-2.2Z" />
    </Svg>
  );
}

/** Matches — a chat bubble. */
export function ChatIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 12a7.5 7.5 0 0 1 7.5-7.5h1A7.5 7.5 0 0 1 20 12a7.5 7.5 0 0 1-7.5 7.5H6l-1.7 1.7a.8.8 0 0 1-1.3-.6V12Z" />
      <path d="M9 11.5h6M9 14h3.5" />
    </Svg>
  );
}

/** Profile — a person. */
export function UserIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8.5" r="3.8" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </Svg>
  );
}

/** Virtual-only profiles — a laptop. */
export function LaptopIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="5" width="17" height="11.5" rx="1.5" />
      <path d="M2 19.5h20" />
    </Svg>
  );
}

/** Countries / worldwide — a globe. */
export function GlobeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.6 2.3 4 5.2 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.2-4-8.5s1.4-6.2 4-8.5Z" />
    </Svg>
  );
}

/** Locations — a map pin. */
export function PinIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </Svg>
  );
}
